/**
 * The shared "you're offline" surface — same visual format as the
 * PWA's service-worker fallback page (core/static/pwa/offline.html):
 * logo, title, one-paragraph body, action buttons, muted hint.
 *
 * Shown as a GLOBAL MODAL via ``showOfflineModal()`` + the singleton
 * ``<OfflineModalHost/>`` (mounted once in ApiProvider). Any surface
 * that hits an offline wall — Discover join, settings save, the Data
 * Manager on a not-downloaded project, the whole web app when the
 * cloud is gone — fires the same modal instead of inventing its own
 * error state.
 *
 * Kinds:
 *   'web'     — browser build, cloud unreachable. Auto-RELOADS when
 *               the cloud answers again (the SPA's failed queries
 *               need a fresh boot).
 *   'action'  — a specific action needed the cloud (join, settings,
 *               roster…). Dismissable; auto-dismisses on reconnect.
 *   'project' — a not-downloaded project's data lives on the cloud.
 *               Hints at the "Download for offline" toggle.
 */
import { useEffect, useState } from "react";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./OfflineCard.prefix.css";

const block = cn("offline-card");

const isDesktopShellHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost") &&
  window.location.port === "17080";

/** True when the CLOUD is reachable. On the desktop the local Django
 *  is always up, so /api/version/ would lie — ask the authoritative
 *  cloud_reachable probe there instead. Exported so callers (e.g.
 *  ApiProvider) can CONFIRM a single 5xx is a real outage before
 *  blocking the whole screen — a lone transient 5xx (an instance
 *  recycling behind nginx) probes clean a moment later. */
export const probeCloud = async () => {
  try {
    if (isDesktopShellHost()) {
      const r = await fetch("/api/afri/auth/cloud_reachable/", {
        credentials: "same-origin",
        cache: "no-store",
      });
      const b = await r.json();
      return !!b?.reachable;
    }
    const r = await fetch("/api/version/", { method: "GET", cache: "no-store" });
    return r.ok || r.status < 500;
  } catch (_) {
    return false;
  }
};

/** Fire the global offline modal. Safe to call from anywhere. */
export const showOfflineModal = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent("afri:offline-modal", { detail }));
  } catch (_) {
    /* very old webviews — nothing to show */
  }
};

export const OfflineCard = ({ title, body, hint, children }) => (
  <div className={block.toClassName()} role="status">
    <img className={block.elem("logo").toClassName()} src="/static/pwa/icon-192.png" alt="AfriAnnotate" />
    <h1 className={block.elem("title").toClassName()}>{title}</h1>
    <p className={block.elem("body").toClassName()}>{body}</p>
    {children && <div className={block.elem("actions").toClassName()}>{children}</div>}
    {hint && <div className={block.elem("hint").toClassName()}>{hint}</div>}
  </div>
);

/** Button styled to match the PWA page's dark pill button. */
export const OfflineCardButton = ({ secondary, ...props }) => (
  <button type="button" className={block.elem("btn").mod({ secondary: !!secondary }).toClassName()} {...props} />
);

/** Tracks whether a host instance is already live. ApiProvider mounts
 *  in THREE places (app root, every <Form>, Modal) — without this
 *  guard each mount opened its own copy of the modal for a single
 *  offline event and the user had to close them one by one. */
let _hostClaimed = false;

/** Singleton modal host. Mounted wherever ApiProvider mounts, but
 *  only the FIRST live instance claims the event listener — the rest
 *  render nothing. Repeated events while open just refresh the
 *  payload, so a burst of failing calls never stacks. */
export const OfflineModalHost = () => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [isPrimary] = useState(() => {
    if (_hostClaimed) return false;
    _hostClaimed = true;
    return true;
  });

  useEffect(() => {
    if (!isPrimary) return undefined;
    const onShow = (e) => setDetail(e.detail || {});
    window.addEventListener("afri:offline-modal", onShow);
    return () => {
      window.removeEventListener("afri:offline-modal", onShow);
      _hostClaimed = false;
    };
  }, [isPrimary]);

  // While open: poll the cloud; when it answers, reload (web — the
  // failed queries need a fresh boot) or just dismiss (everything
  // else — the app behind the modal is still healthy).
  useEffect(() => {
    if (!detail) return undefined;
    let alive = true;
    const tick = async () => {
      if (!(await probeCloud()) || !alive) return;
      if ((detail.kind || "action") === "web") window.location.reload();
      else setDetail(null);
    };
    const id = setInterval(tick, 8000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [detail]);

  if (!isPrimary || !detail) return null;
  const kind = detail.kind || "action";

  const body =
    kind === "web"
      ? t("components.offline_card.web_cloud_down_body")
      : kind === "project"
        ? t("components.offline_card.project_not_downloaded_body")
        : t("components.offline_card.action_needs_connection");
  const hint =
    kind === "project"
      ? t("components.offline_card.desktop_download_hint")
      : kind === "web"
        ? t("components.offline_card.use_apps_hint")
        : t("components.offline_card.offline_action_hint");

  const retry = async () => {
    if (await probeCloud()) {
      if (kind === "web") window.location.reload();
      else setDetail(null);
    }
  };

  return (
    <div
      className={block.elem("modal-backdrop").toClassName()}
      onClick={(e) => {
        if (e.target === e.currentTarget) setDetail(null);
      }}
    >
      <OfflineCard title={t("components.offline_card.you_are_offline")} body={body} hint={hint}>
        <OfflineCardButton onClick={retry}>{t("components.offline_card.try_again")}</OfflineCardButton>
        <OfflineCardButton secondary onClick={() => setDetail(null)}>
          {t("components.offline_card.close")}
        </OfflineCardButton>
      </OfflineCard>
    </div>
  );
};
